import React from 'react';
import { CheckIcon, DatabaseIcon, FileTextIcon, LandmarkIcon, Share2Icon, ShieldCheckIcon, UserCheckIcon } from 'lucide-react';
import { FitScale } from '../digital/FitScale';
import { TRUST_HERO_CITY } from '../../data/trustHero';
import { atnaVerbs, atnaWithTrust, systemGovernance, systemInputs, type Bi, type SystemNodeKey } from '../../data/trustSolution';
type Pick = (b: Bi) => string;

/* Same ATNA logo file as the global site header */
const ATNA_LOGO = 'https://cdn.magicpatterns.com/uploads/d6cqpG2SGUofNqC1hEedXv/Logo_ATNA_(5).png';
const NODE_ICONS: Record<SystemNodeKey, typeof DatabaseIcon> = {
  sources: LandmarkIcon,
  rules: ShieldCheckIcon,
  data: DatabaseIcon,
  systems: Share2Icon,
  oversight: UserCheckIcon,
  audit: FileTextIcon
};

/* Positions on the 680 × 320 design canvas */
const LEFT_POS = [{
  left: 110,
  top: 14
}, {
  left: 62,
  top: 112
}, {
  left: 116,
  top: 214
}];
const RIGHT_POS = [{
  left: 500,
  top: 14
}, {
  left: 546,
  top: 112
}, {
  left: 494,
  top: 214
}];
function Node({
  k,
  label
}: {
  k: SystemNodeKey;
  label: string;
}) {
  const Icon = NODE_ICONS[k];
  return <div className="w-[76px] rounded-xl bg-white/95 border border-[#ECEBF7] shadow-[0_10px_22px_-16px_rgba(79,70,229,0.45)] px-1.5 pt-2.5 pb-2 flex flex-col items-center text-center" data-id="element-7154" data-mp="fjuth3938bn">
      <Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-7155" data-mp="acyctl4to562" />
      <span className="mt-1.5 font-sans text-[10px] leading-tight text-[#121A3A]" data-id="element-7156" data-mp="fc1482rvqcvk">{label}</span>
    </div>;
}
function AtnaCard({
  pick
}: {
  pick: Pick;
}) {
  return <div className="w-[170px] rounded-2xl bg-white border border-[#E6E4F8] shadow-[0_0_0_18px_rgba(241,240,253,0.55),0_24px_48px_-26px_rgba(79,70,229,0.55)] px-5 pt-3 pb-4" data-id="element-7157" data-mp="jb9yr9uques6">
      <div className="flex justify-center" data-id="element-7158" data-mp="t2o6g4wumkwe">
        <img src={ATNA_LOGO} alt="ATNA" className="h-[46px] w-auto object-contain" data-id="element-7159" data-mp="y292o7kmwf5f" />
      </div>
      <ul className="mt-1 space-y-1.5" data-id="element-7160" data-mp="131dfr9aoix11">
        {atnaVerbs.map(v => <li key={v.fr} className="flex items-center gap-2 font-sans text-[12px] text-[#121A3A]" data-id="element-7161" data-mp="12z9cmtklu21t">
            <CheckIcon className="w-3.5 h-3.5 text-[#22A06B]" strokeWidth={2.4} aria-hidden="true" data-id="element-7162" data-mp="db3wpuyk0jni" />
            {pick(v)}
          </li>)}
        <li className="flex items-center gap-2 font-sans text-[12px] font-medium text-[#4F46E5]" data-id="element-7163" data-mp="n39n5095qt0l">
          <CheckIcon className="w-3.5 h-3.5 text-[#A5A0F2]" strokeWidth={2.4} aria-hidden="true" data-id="element-7164" data-mp="ic1ck0w3d1lu" />
          {pick(atnaWithTrust)}
        </li>
      </ul>
    </div>;
}
export function TrustSystemVisual({
  pick
}: {
  pick: Pick;
}) {
  return <>
      <div className="hidden sm:block" data-id="element-7165" data-mp="r6pilt9hqnbp">
        <FitScale width={680} height={320} data-id="element-7166" data-mp="162f5ruqex6i0">
          <div className="relative w-[680px] h-[320px]" data-id="element-7167" data-mp="16144q2ocqcul">
            <div className="absolute inset-0 overflow-hidden" aria-hidden="true" data-id="element-7168" data-mp="112j2xytgbl41">
              <img src={TRUST_HERO_CITY} alt="" className="w-full h-full object-cover opacity-60" data-id="element-7169" data-mp="37syyv4ekdyp2" />
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.65)_0%,rgba(255,255,255,0.4)_40%,#fff_78%)]" data-id="element-7170" data-mp="32ufe37bpisya" />
              <div className="absolute inset-y-0 left-0 w-1/4 bg-[linear-gradient(90deg,#fff_0%,rgba(255,255,255,0)_100%)]" data-id="element-7171" data-mp="1icmzcyn12nfl" />
              <div className="absolute inset-x-0 bottom-0 h-1/4 bg-[linear-gradient(0deg,#fff_0%,rgba(255,255,255,0)_100%)]" data-id="element-7172" data-mp="1nd0vpjf9o8xs" />
            </div>
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 680 320" aria-hidden="true" data-id="element-7173" data-mp="1dkmwoyfkpgfb">
              <g fill="none" stroke="#9D97F1" strokeWidth="1" data-id="element-7174" data-mp="zkkefb3cskzj">
                <path d="M188 40 C 230 36, 250 60, 262 88" data-id="element-7175" data-mp="13n8m3fgbzlux" />
                <path d="M140 150 C 190 150, 220 170, 255 180" data-id="element-7176" data-mp="13ojsln15v7pq" />
                <path d="M194 250 C 225 250, 245 235, 258 215" data-id="element-7177" data-mp="po92wnbl7z65" />
                <path d="M500 40 C 460 36, 440 60, 425 88" data-id="element-7178" data-mp="umixavphmvqp" />
                <path d="M546 150 C 500 150, 470 165, 430 170" data-id="element-7179" data-mp="un2e293eje35" />
                <path d="M494 250 C 460 250, 440 238, 428 218" data-id="element-7180" data-mp="2k178itmce7n5" />
              </g>
            </svg>
            <div className="absolute left-[255px] top-[58px]" data-id="element-7181" data-mp="2p0tlr3267nvy">
              <AtnaCard pick={pick} data-id="element-7182" data-mp="2oy7dt2bdb6d7" />
            </div>
            {systemInputs.map((n, i) => <div key={n.key} className="absolute" style={LEFT_POS[i]} data-id="element-7183" data-mp="ovy31cglyzt7" data-mpi={i}>
                <Node k={n.key} label={pick(n.label)} data-id="element-7184" data-mp="k00nh8d65xhh" />
              </div>)}
            {systemGovernance.map((n, i) => <div key={n.key} className="absolute" style={RIGHT_POS[i]} data-id="element-7185" data-mp="ayj7dpzrhvss" data-mpi={i}>
                <Node k={n.key} label={pick(n.label)} data-id="element-7186" data-mp="b0nai5pu6qs0" />
              </div>)}
          </div>
        </FitScale>
      </div>

      {/* Mobile */}
      <div className="sm:hidden flex flex-col items-center gap-3" data-id="element-7187" data-mp="17ongs9zn8ai">
        <div className="grid grid-cols-3 gap-2 w-full" data-id="element-7188" data-mp="3sf0wgk0pwffc">
          {systemInputs.map(n => <Node key={n.key} k={n.key} label={pick(n.label)} data-id="element-7189" data-mp="3odfnvav7unay" />)}
        </div>
        <span className="w-px h-4 bg-[#C9C5F6]" aria-hidden="true" data-id="element-7190" data-mp="3obmdplle9ky7" />
        <AtnaCard pick={pick} data-id="element-7191" data-mp="3jdtan1fe1725" />
        <span className="w-px h-4 bg-[#C9C5F6]" aria-hidden="true" data-id="element-7192" data-mp="3jg74x828uf8n" />
        <div className="grid grid-cols-3 gap-2 w-full" data-id="element-7193" data-mp="3efrna4axilkv">
          {systemGovernance.map(n => <Node key={n.key} k={n.key} label={pick(n.label)} data-id="element-7194" data-mp="1twz2ezk05p3v" />)}
        </div>
      </div>
    </>;
}